import React from 'react';

import LaunchIcon from '@mui/icons-material/Launch';
import LockIcon from '@mui/icons-material/Lock';
import { Alert, Box, Divider, Stack, TextField, Typography } from '@mui/material';

import CustomButton from 'components/button/CustomButton';
import TextArea from 'components/form/TextArea';
import KnzViewer from 'components/knz/KnzViewer';
import { MSG_REQUIRED } from 'constant/ValidationMessages';
import { QuestionDetailResult, updateQuestion } from 'stores/TrainingAdminStoreAdapter';
import { syncQuestion } from 'stores/TrainingQuestionStoreAdapter';
import { getFileDetail, getWorkSpace, saveFile } from 'stores/WorkSpaceStoreAdapter';
import { errorDefault, successDefault, toErrorDetail } from 'utils/Toastify';

import AnswerCodesEditor, { EditableAnswerCode } from './AnswerCodesEditor';
import { fromAnswerCodeResults, toAnswerCodeInputs, validateAnswerCodes } from './answerCodeUtils';
import { buildFileIdToPath, rewriteAnswerBlocksInKnz } from './questionKnzSerialize';

type Props = {
  question: QuestionDetailResult;
  onUpdated: () => void;
};

const TITLE_MAX = 128;
const CONTENT_MAX = 100000;

/**
 * 設問編集フォーム。source_type で挙動が変わる。
 * - FORM: title / 本文（md）/ 正解コード・期待値をすべて編集可
 * - KNZ: title・本文は KnzViewer でビュー表示（編集不可・workspaceで編集導線）。
 *        正解コード・期待値のみ編集可 → knz を書き換えて保存（API6107）＋反映（API6316）
 */
const QuestionUpdateForm: React.FC<Props> = ({ question, onUpdated }) => {
  const isKnz = question.sourceType === 'KNZ';
  const [submitting, setSubmitting] = React.useState(false);

  const [title, setTitle] = React.useState(question.title);
  const [content, setContent] = React.useState(question.content ?? '');
  const [answerCodes, setAnswerCodes] = React.useState<EditableAnswerCode[]>(
    fromAnswerCodeResults(question.answerCodes),
  );
  const [knzSource, setKnzSource] = React.useState<string>('');

  React.useEffect(() => {
    setTitle(question.title);
    setContent(question.content ?? '');
    setAnswerCodes(fromAnswerCodeResults(question.answerCodes));
    // KNZ 設問は本文ビュー表示のため knz ソースを取得
    if (question.sourceType === 'KNZ' && question.sourceFileId) {
      getFileDetail(question.sourceFileId)
        .then((detail) => setKnzSource(detail.detail ?? ''))
        .catch(() => errorDefault('knzファイルの読み込みに失敗しました'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.questionId]);

  const openInWorkspace = () => {
    // R-9: source_file_id のファイルを開いた状態で workspace を別タブで開く
    if (!question.sourceFileId) {
      window.open('/workspace', '_blank');
      return;
    }
    window.open(`/workspace?fileId=${encodeURIComponent(question.sourceFileId)}`, '_blank');
  };

  const submitForm = async () => {
    if (title.trim() === '') {
      errorDefault(MSG_REQUIRED('設問タイトル'));
      return;
    }
    if (content.trim() === '') {
      errorDefault(MSG_REQUIRED('設問本文（Markdown）'));
      return;
    }
    const validationError = validateAnswerCodes(answerCodes);
    if (validationError) {
      errorDefault(validationError);
      return;
    }
    setSubmitting(true);
    try {
      await updateQuestion({
        questionId: question.questionId,
        title: title.trim(),
        content,
        answerCodes: toAnswerCodeInputs(answerCodes),
      });
      successDefault('設問を更新しました');
      onUpdated();
    } catch (e) {
      errorDefault('設問の更新に失敗しました', toErrorDetail(e));
    } finally {
      setSubmitting(false);
    }
  };

  const submitKnz = async () => {
    const validationError = validateAnswerCodes(answerCodes);
    if (validationError) {
      errorDefault(validationError);
      return;
    }
    if (!question.sourceFileId) {
      errorDefault('knzファイルが見つかりません');
      return;
    }
    setSubmitting(true);
    try {
      // ワークスペースツリーから fileId → path を作り、knz にはパスで書き出す
      const nodes = await getWorkSpace();
      const idToPath = buildFileIdToPath(nodes);
      // 1. knz の AnswerCode/Expected ブロックを書き換える（本文は保持）
      const newSource = rewriteAnswerBlocksInKnz(knzSource, answerCodes, idToPath);
      // 2. 最新 version を取得して保存（API6107・純粋保存）
      const detail = await getFileDetail(question.sourceFileId);
      await saveFile({ fileId: question.sourceFileId, detail: newSource, version: detail.version });
      setKnzSource(newSource);
      // 3. 反映（API6316）: フロントがパースした構造化データを送る
      await syncQuestion({
        sourceFileId: question.sourceFileId,
        title: question.title,
        answerCodes: toAnswerCodeInputs(answerCodes),
      });
      successDefault('正解コード・期待値を反映しました');
      onUpdated();
    } catch (e) {
      errorDefault('反映に失敗しました', toErrorDetail(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      {isKnz ? (
        <Stack spacing={2}>
          <Alert
            severity="info"
            icon={<LockIcon fontSize="small" />}
            action={
              <CustomButton
                label="workspaceで編集"
                buttonVariant="secondary"
                icon={<LaunchIcon />}
                onClick={openInWorkspace}
              />
            }
            sx={{ borderRadius: 2 }}
          >
            設問タイトル・本文は knz で管理されており編集できません。workspace の knz
            エディタで編集してください。
          </Alert>
          {/* 本文ビュー（編集不可） */}
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            {knzSource ? (
              <KnzViewer source={knzSource} tocMode="ignore" />
            ) : (
              <Box sx={{ p: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  knz を読み込み中...
                </Typography>
              </Box>
            )}
          </Box>
          <Divider />
          <Typography variant="caption" color="text.secondary">
            正解コード・期待値のみフォームで編集できます。反映すると knz も書き換えて同期します。
          </Typography>
          <AnswerCodesEditor answerCodes={answerCodes} onChange={setAnswerCodes} />
          <Stack direction="row" justifyContent="flex-end">
            <CustomButton
              label="正解コード・期待値を反映"
              buttonVariant="primary"
              onClick={submitKnz}
              disabled={submitting}
            />
          </Stack>
        </Stack>
      ) : (
        <Stack spacing={2}>
          <TextField
            label="設問タイトル"
            required
            size="small"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            slotProps={{ htmlInput: { maxLength: TITLE_MAX } }}
            helperText={`${title.length}/${TITLE_MAX}`}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <TextArea
            label="設問本文（Markdown）"
            required
            value={content}
            onChange={setContent}
            maxLength={CONTENT_MAX}
            rows={8}
          />
          <Divider />
          <AnswerCodesEditor answerCodes={answerCodes} onChange={setAnswerCodes} />
          <Stack direction="row" justifyContent="flex-end">
            <CustomButton
              label="更新"
              buttonVariant="primary"
              onClick={submitForm}
              disabled={submitting}
            />
          </Stack>
        </Stack>
      )}
    </Box>
  );
};

export default QuestionUpdateForm;
